import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "components/translations/useTranslation";
import Comment from "components/input/Comment.component";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { QuantitySelector } from "components/common/QuantitySelector.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { formatDate, formatCalendarDate } from "utils/valueFormatter.function";
import Datepicker from "react-flatpickr";
import { postponeTasks } from "components/modals/modal.actions";
import ErrorModal from "./Error.modal";
import { useSelector, useDispatch } from "react-redux";
import { Store } from "redux";
import * as reduxSelectors from "components/store/application.reducers";
import { RadioButton } from "components/common/RadioButton.component";
import { FormField } from "components/common/FormField.component";
import { ModalContentProps } from "types/modals";

const RADIO_SELECTION = {
    daysBefore: 1,
    daysFromNow: 2,
    thisDay: 3,
} as const;

type RadioSelectionValue = typeof RADIO_SELECTION[keyof typeof RADIO_SELECTION];

const getTomorrow = (): Date => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
};

interface PostponeModalProps extends ModalContentProps {
    more: {
        tasks: Array<Record<string, any>>;
    };
}

const Postpone = ({ more, closeModal, loopFocus }: PostponeModalProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const initComment = useSelector((store: Store) => reduxSelectors.getTaskDraftCommentText(store));
    const commentInputRef = useRef<HTMLDivElement>(null);

    const [comment, setComment] = useState(initComment);
    const [selectedRemindMe, setSelectedRemindMe] = useState<RadioSelectionValue>(RADIO_SELECTION.daysFromNow);
    const [daysBefore, setDaysBefore] = useState(1);
    const [daysFromNow, setDaysFromNow] = useState(1);
    const [thisDay, setThisDay] = useState(getTomorrow);
    const [errorState, setErrorState] = useState<Record<string, string> | null>(null);

    useEffect(() => {
        commentInputRef.current?.focus();
    }, []);

    const handlePostponeError = useCallback(() => {
        setErrorState({
            errorType: "WARNING",
            errorTitle: translate("popUp.postpone.title"),
            errorMessages: translate("popUp.postpone.actionError"),
        });
    }, [translate]);

    const handlePostpone = useCallback(() => {
        const taskKeys = more.tasks?.map((task) => task.key);
        const postponeMessage = translate("popUp.postpone.actionSuccess", taskKeys.length);

        const dayFields = {
            daysBefore: selectedRemindMe === RADIO_SELECTION.daysBefore ? daysBefore : undefined,
            daysFromNow: selectedRemindMe === RADIO_SELECTION.daysFromNow ? daysFromNow : undefined,
            thisDay: selectedRemindMe === RADIO_SELECTION.thisDay
                ? formatCalendarDate(thisDay)
                : undefined,
        };

        dispatch(postponeTasks(taskKeys, comment, dayFields, postponeMessage, closeModal, handlePostponeError));
    }, [more.tasks, translate, selectedRemindMe, daysBefore, daysFromNow, thisDay, dispatch, comment, closeModal, handlePostponeError]);

    if (errorState) {
        return (
            <ErrorModal
                more={{ error: errorState }}
                closeModal={() => setErrorState(null)}
                loopFocus={loopFocus}
            />
        );
    }

    return (
        <>
            <div className="ga-modal__content">
                <form>
                    <RemindMe
                        tasks={more.tasks}
                        onChange={setSelectedRemindMe}
                        translate={translate}
                        selectedRemindMe={selectedRemindMe}
                        daysBefore={daysBefore}
                        daysFromNow={daysFromNow}
                        thisDay={thisDay}
                        onDaysBeforeChange={setDaysBefore}
                        onDaysFromNowChange={setDaysFromNow}
                        onThisDayChange={setThisDay}
                    />
                    <Comment
                        id="comment"
                        inputHint={translate("popUp.postpone.commentHint")}
                        translate={translate}
                        propagateValue={setComment}
                        value={comment}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                        label={translate("popUp.postpone.commentLabel")}
                        labelState={`(${translate("popUp.required")})`}
                    />
                </form>
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={!!comment?.trim() && selectedRemindMe !== null}
                okCallback={handlePostpone}
                okText={translate("popUp.postpone.postponeTask")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.close")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default Postpone;

interface RemindMeProps {
    tasks: Array<Record<string, any>>;
    onChange: (value: RadioSelectionValue) => void;
    selectedRemindMe: RadioSelectionValue;
    translate: (key: string, ...args: any[]) => string;
    daysBefore: number;
    daysFromNow: number;
    thisDay: Date;
    onDaysBeforeChange: (value: number) => void;
    onDaysFromNowChange: (value: number) => void;
    onThisDayChange: (value: Date) => void;
}

const getMinDocumentDueDate = (tasks: Array<Record<string, any>>): string | null => {
    return tasks.reduce<string | null>((acc, val) => {
        if (acc === null) return val.documentDueDate;
        return acc < val.documentDueDate ? acc : val.documentDueDate;
    }, null);
};

const RemindMe = ({
    tasks,
    onChange,
    selectedRemindMe,
    translate,
    daysBefore,
    daysFromNow,
    thisDay,
    onDaysBeforeChange,
    onDaysFromNowChange,
    onThisDayChange,
}: RemindMeProps): React.ReactElement => {
    const tomorrow = getTomorrow();
    const minDocDueDate = formatDate(getMinDocumentDueDate(tasks), null);
    const daysFromNowRelative = formatDate(tomorrow, null);

    return (
        <FormField
            id="postponeRemindMe"
            label={translate("popUp.postpone.remindMe")}
        >
            <div className="d-grid" style={{ gridTemplateColumns: "1fr 1fr", rowGap: "8px" }}>
                <div>
                    <RadioButton
                        name="optradio"
                        radioId="daysBefore"
                        value={String(RADIO_SELECTION.daysBefore)}
                        isChecked={selectedRemindMe === RADIO_SELECTION.daysBefore}
                        onChange={() => onChange(RADIO_SELECTION.daysBefore)}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                        label={translate("popUp.postpone.daysBeforeDoc")}
                    />
                </div>
                <div>
                    <FormField
                        id="daysBeforeQuantity"
                        info={{ label: translate("popUp.postpone.daysBeforeDocRelative", minDocDueDate) }}
                    >
                        <QuantitySelector
                            disabled={selectedRemindMe !== RADIO_SELECTION.daysBefore}
                            value={daysBefore}
                            ariaLabel={translate("popUp.postpone.daysBeforeDoc")}
                            onChange={(e) => onDaysBeforeChange(parseInt(e.target.value, 10) || 1)}
                            min={1}
                            max={99}
                            step={1}
                            tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                        />
                    </FormField>
                </div>

                <div>
                    <RadioButton
                        name="optradio"
                        radioId="dayFromNow"
                        value={String(RADIO_SELECTION.daysFromNow)}
                        isChecked={selectedRemindMe === RADIO_SELECTION.daysFromNow}
                        onChange={() => onChange(RADIO_SELECTION.daysFromNow)}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 3}
                        label={translate("popUp.postpone.daysAfterTaskDueDate")}
                    />
                </div>
                <div>
                    <FormField id="daysFromNowQuantity">
                        <QuantitySelector
                            disabled={selectedRemindMe !== RADIO_SELECTION.daysFromNow}
                            value={daysFromNow}
                            ariaLabel={translate("popUp.postpone.daysAfterTaskDueDate")}
                            onChange={(e) => onDaysFromNowChange(parseInt(e.target.value, 10) || 1)}
                            min={1}
                            max={99}
                            step={1}
                            tabIndex={MODAL_CONTENT_TAB_INDEX + 4}
                        />
                    </FormField>
                </div>

                <div>
                    <RadioButton
                        name="optradio"
                        radioId="thisDay"
                        value={String(RADIO_SELECTION.thisDay)}
                        isChecked={selectedRemindMe === RADIO_SELECTION.thisDay}
                        onChange={() => onChange(RADIO_SELECTION.thisDay)}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 5}
                        label={translate("popUp.postpone.onThisDate")}
                    />
                </div>
                <div>
                    <FormField id="thisDayPicker">
                        <Datepicker
                            options={{
                                allowInput: true,
                                defaultDate: tomorrow,
                                minDate: tomorrow,
                                dateFormat: "d/m/Y",
                            }}
                            aria-label={translate("popUp.postpone.onThisDate")}
                            disabled={selectedRemindMe !== RADIO_SELECTION.thisDay}
                            defaultValue={daysFromNowRelative}
                            value={thisDay}
                            onChange={(selectedDates) => onThisDayChange(selectedDates[0])}
                            className="flatpickr border px-2"
                        />
                    </FormField>
                </div>
            </div>
        </FormField>
    );
};
